import React from 'react';
import { useTranslation } from 'react-i18next';
import { ListColumnType } from 'types/ListTypes';
import AbstractList from 'components/lists/AbstractList';
import { useSettings } from 'services/settings/SettingsContext';
import { formatDate } from 'services/utils/dateTimeUtils';
import { convertUTCToLocalTimeString } from 'internationalization/timezoneConversions';
import { SubcontractorDayPassType } from './SubcontractorTypes';

// The usage tab with the consumed day passes, newest first
const SubcontractorUsageList: React.FC = () => {
    const { t } = useTranslation();
    const { userLocale, userTimezone, getTimeFormat } = useSettings();

    // Specify the options of this list:
    const query = {
        endpoint: 'get_subcontractor_day_pass_list',
        defaultParams: {},
        object: 'subcontractor_day_pass'
    }
    const objectName = 'subcontractor.day_pass.object_name'
    const isPaginated = true

    // Render the date of the pass with the start and end time of its planning when known
    const renderDate = (row: SubcontractorDayPassType) => {
        const date = formatDate(row.date, userLocale, 'writtenMonth');
        if (!row.start_datetime) return date;

        // Format the times in the timezone of the user
        const is12HourFormat = getTimeFormat() === '12h';
        const startTime = convertUTCToLocalTimeString(row.start_datetime, userLocale, userTimezone, is12HourFormat);
        const endTime = row.end_datetime ? convertUTCToLocalTimeString(row.end_datetime, userLocale, userTimezone, is12HourFormat) : null;
        return endTime ? `${date}, ${startTime} - ${endTime}` : `${date}, ${startTime}`;
    };

    // Render the name of the subcontractor with the company name behind it
    const renderSubcontractor = (row: SubcontractorDayPassType) => {
        return row.user_company_name ? `${row.user_name} (${row.user_company_name})` : row.user_name;
    };

    // Specify the columns of this list:
    const columns: ListColumnType[] = [
        { width: '2fr', field: 'date', label: 'subcontractor.day_pass.date_label', render: (_value, item) => <span>{renderDate(item as unknown as SubcontractorDayPassType)}</span> },
        { width: '2fr', field: 'user_name', label: 'subcontractor.day_pass.subcontractor_label', render: (_value, item) => <span>{renderSubcontractor(item as unknown as SubcontractorDayPassType)}</span> },
        { width: '1fr', field: 'job_number', label: 'subcontractor.day_pass.job_label' },
    ];

    return (
        <AbstractList
            query={query}
            objectName={objectName}
            columns={columns}
            showSearch={false}
            isPaginated={isPaginated}
            showAddButton={false}
        />
    );
};

export default SubcontractorUsageList;
